import { useState, type ReactNode } from 'react';

import { api } from '../api';
import { EditorRegra } from './EditorRegra';
import {
  Aviso,
  Campo,
  Cartao,
  Corte,
  data,
  Estado,
  Etiqueta,
  Painel,
  Tempo,
  useAcao,
  useCarga,
} from '../comum';
import type { PainelSentinela, Regra, Remediacao, Violacao } from '../tipos';

const ROTULO_VIABILIDADE: Record<
  string,
  { texto: string; classe: 'ok' | 'alerta' | 'erro' | 'neutro' }
> = {
  apto: { texto: 'Apto', classe: 'ok' },
  apto_com_ressalva: { texto: 'Apto com ressalva', classe: 'alerta' },
  inapto: { texto: 'Inapto por hardware', classe: 'erro' },
  // Indeterminado é neutro de propósito: não é problema da máquina, é dado que
  // ninguém coletou. Pintar de vermelho acusaria o parque por falha nossa.
  indeterminado: { texto: 'Sem dados para decidir', classe: 'neutro' },
  ja_atende: { texto: 'Já atende', classe: 'ok' },
};

/**
 * Sentinela — conformidade contínua.
 *
 * Três estados, não dois: conforme, violando e **indeterminado**. Máquina sem
 * dado coletado não some do relatório nem vira acusação.
 */
/** Quantas violações a tabela desenha. Acima disso, `Corte` avisa. */
const TETO_VIOLACOES = 200;

export function Sentinela({
  aoPerderSessao,
  podeAprovar,
  podeDispensar,
  podeEditarRegra,
  podeRegraComRemediacao,
}: {
  aoPerderSessao: () => void;
  /** `aprovar_remediacao` — exige `scripts: WRITE`. Dispara script na máquina. */
  podeAprovar: boolean;
  /** `dispensar_violacao` — silenciar um alerta é decisão sobre o parque. */
  podeDispensar: boolean;
  /** `editar_regra` — exige `machines: WRITE`. Define política do parque. */
  podeEditarRegra: boolean;
  /** `editar_regra_com_remediacao` — exige `machines` E `scripts` em WRITE. */
  podeRegraComRemediacao: boolean;
}): ReactNode {
  /**
   * 🔴 Mexer numa regra que remedia exige mais do que mexer numa que verifica.
   *
   * O backend decide isso pelo `modo` do corpo enviado
   * (`apps/app/src/rotas/sentinela.ts`), e a tela precisa usar a mesma conta —
   * senão o botão aparece e a gravação leva 403 depois do trabalho feito.
   */
  const podeMexerNa = (modo: string): boolean =>
    modo === 'remediar' ? podeRegraComRemediacao : podeEditarRegra;
  const acao = useAcao(aoPerderSessao);
  const [aberta, setAberta] = useState<string | null>(null);
  const [dispensando, setDispensando] = useState<string | null>(null);
  const [motivoDispensa, setMotivoDispensa] = useState('');
  const [editor, setEditor] = useState<
    { modo: 'nova' } | { modo: 'editar'; regra: Record<string, unknown> } | null
  >(null);

  const painel = useCarga<PainelSentinela>(
    () => api.get('/api/sentinela/painel'),
    [],
    aoPerderSessao,
  );
  const regras = useCarga<{ itens: readonly Regra[] }>(
    () => api.get('/api/regras'),
    [],
    aoPerderSessao,
  );
  const violacoes = useCarga<{ itens: readonly Violacao[] }>(
    () => api.get('/api/violacoes'),
    [],
    aoPerderSessao,
  );
  const remediacoes = useCarga<{ itens: readonly Remediacao[] }>(
    () => api.get('/api/remediacoes'),
    [],
    aoPerderSessao,
  );

  const avaliarTudo = (): void => {
    void acao.rodar(async () => {
      const r = await api.post<{
        regrasAvaliadas: number;
        totalNovas: number;
        totalResolvidas: number;
      }>('/api/sentinela/avaliar-tudo');
      painel.recarregar();
      violacoes.recarregar();
      return `${r.regrasAvaliadas} regra(s) avaliadas · ${r.totalNovas} nova(s) · ${r.totalResolvidas} resolvida(s).`;
    });
  };

  return (
    <>
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      <Estado carga={painel}>
        {painel.dados !== null && (
          <div className="cartoes">
            <Cartao rotulo="Regras ativas" valor={painel.dados.regrasAtivas} />
            <Cartao rotulo="Violações abertas" valor={painel.dados.violacoesAbertas} destaque />
            <Cartao rotulo="Remediáveis" valor={painel.dados.remediaveis} />
            <Cartao rotulo="Bloqueadas por hardware" valor={painel.dados.bloqueadosPorHardware} />
            <Cartao rotulo="Sem dados para decidir" valor={painel.dados.semDadosParaDecidir} />
          </div>
        )}
      </Estado>

      <div className="linha">
        <span className="cresce" />
        {podeEditarRegra && editor === null && (
          <button className="primario encolhe" onClick={() => setEditor({ modo: 'nova' })}>
            Nova regra
          </button>
        )}
        <button className="encolhe" disabled={acao.executando} onClick={avaliarTudo}>
          {acao.executando ? 'Avaliando…' : 'Avaliar todas as regras'}
        </button>
      </div>

      {editor !== null && (
        <EditorRegra
          regraExistente={editor.modo === 'editar' ? editor.regra : null}
          podeRemediacao={podeRegraComRemediacao}
          aoFechar={() => setEditor(null)}
          aoSalvar={() => {
            setEditor(null);
            regras.recarregar();
            painel.recarregar();
          }}
          aoPerderSessao={aoPerderSessao}
        />
      )}

      <Painel titulo="Regras">
        <Estado carga={regras}>
          {regras.dados !== null &&
            (regras.dados.itens.length === 0 ? (
              <p className="suave">
                Nenhuma regra cadastrada. Uma regra é escopo (a quem se aplica) mais exigência (o
                que precisa ser verdade).
              </p>
            ) : (
              <div className="rolagem">
                <table className="densa">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Severidade</th>
                      <th>Modo</th>
                      <th>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {regras.dados.itens.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {r.nome}
                          {r.descricao !== '' && (
                            <div className="suave" style={{ fontSize: 13 }}>
                              {r.descricao}
                            </div>
                          )}
                        </td>
                        <td>
                          <Etiqueta
                            tom={
                              r.severidade === 'critica' || r.severidade === 'alta'
                                ? 'erro'
                                : r.severidade === 'media'
                                  ? 'alerta'
                                  : 'neutro'
                            }
                          >
                            {r.severidade}
                          </Etiqueta>
                        </td>
                        <td className="suave">{r.modo}</td>
                        <td>
                          <Etiqueta tom={r.ativa ? 'ok' : 'neutro'}>
                            {r.ativa ? 'ativa' : 'desativada'}
                          </Etiqueta>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            disabled={acao.executando}
                            title="Roda a regra agora e grava as violações"
                            onClick={() =>
                              void acao.rodar(async () => {
                                const x = await api.post<{
                                  violando: number;
                                  diff: { novas: number; resolvidas: number };
                                }>(`/api/regras/${encodeURIComponent(r.id)}/avaliar`);
                                painel.recarregar();
                                violacoes.recarregar();
                                return `${x.violando} violando · ${x.diff.novas} nova(s).`;
                              })
                            }
                          >
                            Avaliar
                          </button>{' '}
                          {podeAprovar && r.modo === 'remediar' && (
                            <>
                              <button
                                disabled={acao.executando}
                                title="Monta a onda com as máquinas aptas e pede aprovação"
                                onClick={() =>
                                  void acao.rodar(async () => {
                                    const x = await api.post<{
                                      alvos: number;
                                      ondas: number;
                                      excluidas: readonly unknown[];
                                    }>(`/api/regras/${encodeURIComponent(r.id)}/remediar`);
                                    remediacoes.recarregar();
                                    return (
                                      `Remediação criada: ${x.alvos} alvo(s) em ${x.ondas} onda(s)` +
                                      (x.excluidas.length > 0
                                        ? ` · ${x.excluidas.length} fora por falta de viabilidade`
                                        : '') +
                                      '. Aguardando aprovação.'
                                    );
                                  })
                                }
                              >
                                Remediar
                              </button>{' '}
                            </>
                          )}
                          {podeMexerNa(r.modo) && (
                            <>
                              <button
                                disabled={acao.executando}
                                onClick={() =>
                                  void acao.rodar(async () => {
                                    await api.put(`/api/regras/${encodeURIComponent(r.id)}`, {
                                      ativa: !r.ativa,
                                    });
                                    regras.recarregar();
                                    return r.ativa ? 'Regra desativada.' : 'Regra ativada.';
                                  })
                                }
                              >
                                {r.ativa ? 'Desativar' : 'Ativar'}
                              </button>{' '}
                              <button
                                onClick={() =>
                                  setEditor({
                                    modo: 'editar',
                                    regra: r as unknown as Record<string, unknown>,
                                  })
                                }
                              >
                                Editar
                              </button>{' '}
                              <button
                                className="perigo"
                                disabled={acao.executando}
                                onClick={() =>
                                  void acao.rodar(async () => {
                                    await api.del(`/api/regras/${encodeURIComponent(r.id)}`);
                                    regras.recarregar();
                                    painel.recarregar();
                                    return 'Regra excluída.';
                                  })
                                }
                              >
                                Excluir
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </Estado>
      </Painel>

      <Painel
        titulo="Violações abertas"
        sub="Máquina sem dado coletado aparece como indeterminada — não como violação."
      >
        <Estado carga={violacoes}>
          {violacoes.dados !== null &&
            (violacoes.dados.itens.length === 0 ? (
              <p className="suave">Nenhuma violação aberta.</p>
            ) : (
              <div className="rolagem">
                <table className="densa">
                  <thead>
                    <tr>
                      <th>Dispositivo</th>
                      <th>Viabilidade</th>
                      <th>Detectada</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {violacoes.dados.itens.slice(0, TETO_VIOLACOES).map((v) => {
                      const rot = ROTULO_VIABILIDADE[v.violacao.viabilidade ?? ''] ?? null;
                      return (
                        <tr key={v.violacao.id}>
                          <td>{v.dispositivoNome}</td>
                          <td>
                            {rot === null ? (
                              <span className="suave">não analisada</span>
                            ) : (
                              <Etiqueta tom={rot.classe}>{rot.texto}</Etiqueta>
                            )}
                          </td>
                          <td>
                            <Tempo iso={v.violacao.detectadaEm} alertarApos={7} />
                          </td>
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {/*
                              🔴 Sem isto, uma exceção aceita — o servidor
                              legado que vai ficar no Windows 10 mesmo — fica no
                              contador de violações para sempre. E esse contador
                              aparece no painel, no relatório e no resumo que vai
                              para a diretoria.
                            */}
                            {podeDispensar && (
                              <button
                                onClick={() => setDispensando(v.violacao.id)}
                                title="Marcar como exceção aceita, com motivo registrado"
                              >
                                Dispensar
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/*
                  🔴 Corte anunciado.
                  Cortava em 200 em silêncio. Num parque grande, uma regra ampla
                  passa disso no primeiro dia — e quem lê 200 violações acha que
                  são todas.
                */}
                <Corte
                  mostrando={Math.min(violacoes.dados.itens.length, TETO_VIOLACOES)}
                  total={violacoes.dados.itens.length}
                  unidade="violações"
                  comoVerTudo="Estreite o filtro para ver o resto."
                />
              </div>
            ))}
        </Estado>
      </Painel>

      {/*
        A dispensa exige motivo — o servidor recusa sem ele.
        Dispensar sem justificativa transforma o Sentinela em alarme que todo
        mundo desliga; o motivo é o que permite revisar a decisão depois.
      */}
      {dispensando !== null && (
        <div className="editor-cartao">
          <header>
            <strong>Dispensar violação</strong>
            <button
              className="icone"
              onClick={() => {
                setDispensando(null);
                setMotivoDispensa('');
              }}
              aria-label="Fechar"
            >
              ✕
            </button>
          </header>
          <Aviso>
            A máquina sai do contador de violações e não gera novos avisos por esta regra. O motivo
            fica registrado com o seu nome.
          </Aviso>
          <Campo
            rotulo="Por que esta máquina é exceção?"
            ajuda="Ex.: servidor legado do sistema X, troca prevista para o 2º semestre."
          >
            <input
              value={motivoDispensa}
              maxLength={300}
              autoFocus
              onChange={(e) => setMotivoDispensa(e.target.value)}
            />
          </Campo>
          <div className="linha">
            <button
              className="primario encolhe"
              disabled={acao.executando || motivoDispensa.trim().length < 5}
              onClick={() =>
                void acao.rodar(async () => {
                  await api.post(`/api/violacoes/${encodeURIComponent(dispensando)}/dispensar`, {
                    motivo: motivoDispensa.trim(),
                  });
                  setDispensando(null);
                  setMotivoDispensa('');
                  violacoes.recarregar();
                  painel.recarregar();
                  return 'Violação dispensada. O motivo ficou registrado.';
                })
              }
            >
              Dispensar
            </button>
            <button
              className="encolhe"
              onClick={() => {
                setDispensando(null);
                setMotivoDispensa('');
              }}
            >
              Cancelar
            </button>
            <span className="cresce" />
          </div>
        </div>
      )}

      <Painel titulo="Remediações">
        <Estado carga={remediacoes}>
          {remediacoes.dados !== null &&
            (remediacoes.dados.itens.length === 0 ? (
              <p className="suave">Nenhuma remediação criada.</p>
            ) : (
              <div className="rolagem">
                <table className="densa">
                  <thead>
                    <tr>
                      <th>Criada</th>
                      <th>Estado</th>
                      <th>Alvos</th>
                      <th>Progresso</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {remediacoes.dados.itens.map((r) => (
                      <tr key={r.id}>
                        <td className="suave">{data(r.criadaEm)}</td>
                        <td>
                          <span
                            className={`etiqueta ${
                              r.estado === 'concluida'
                                ? 'ok'
                                : r.estado === 'interrompida_por_falhas' || r.estado === 'rejeitada'
                                  ? 'erro'
                                  : 'alerta'
                            }`}
                          >
                            {r.estado.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>{r.alvos.length}</td>
                        <td className="suave">
                          {r.sucessos} ok · {r.falhas} falha(s)
                        </td>
                        <td>
                          <button onClick={() => setAberta(r.id)}>Abrir</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </Estado>
      </Painel>

      {aberta !== null && (
        <DetalheRemediacao
          id={aberta}
          podeAprovar={podeAprovar}
          aoFechar={() => {
            setAberta(null);
            remediacoes.recarregar();
          }}
          aoPerderSessao={aoPerderSessao}
        />
      )}
    </>
  );
}

function DetalheRemediacao({
  id,
  podeAprovar,
  aoFechar,
  aoPerderSessao,
}: {
  id: string;
  podeAprovar: boolean;
  aoFechar: () => void;
  aoPerderSessao: () => void;
}): ReactNode {
  const carga = useCarga<Remediacao>(
    () => api.get(`/api/remediacoes/${encodeURIComponent(id)}`),
    [id],
    aoPerderSessao,
  );
  const acao = useAcao(aoPerderSessao);
  const [motivo, setMotivo] = useState('');

  const r = carga.dados;

  const chamar = (rota: string, corpo: unknown, msg: string): void => {
    void acao.rodar(async () => {
      await api.post(`/api/remediacoes/${encodeURIComponent(id)}/${rota}`, corpo);
      carga.recarregar();
      return msg;
    });
  };

  const executar = (): void => {
    void acao.rodar(async () => {
      const res = await api.post<{
        ok: boolean;
        kind: string;
        ondas?: number;
        sucessos?: number;
        falhas?: number;
        motivo?: string;
      }>(`/api/remediacoes/${encodeURIComponent(id)}/executar`);
      carga.recarregar();
      return res.ok
        ? `Concluída: ${res.ondas ?? 0} onda(s), ${res.sucessos ?? 0} sucesso(s).`
        : `Interrompida: ${res.motivo ?? ''}`;
    });
  };

  return (
    <div
      className="gaveta"
      onClick={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div>
        <div className="linha">
          <h2 style={{ margin: 0 }}>Remediação</h2>
          <button className="encolhe" onClick={aoFechar}>
            Fechar
          </button>
        </div>

        {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
        {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

        <Estado carga={carga}>
          {r !== null && (
            <>
              <p className="suave">
                Estado <strong>{r.estado.replace(/_/g, ' ')}</strong> · script #{r.scriptIdNoKace} ·
                ondas de {r.tamanhoDaOnda} · {r.alvos.length} alvo(s)
                {r.aprovadaPor !== null && <> · aprovada por {r.aprovadaPor}</>}
                {r.ticketIdNoKace !== null && <> · chamado #{r.ticketIdNoKace} no KACE</>}
              </p>

              {r.interrompidaMotivo !== null && <Aviso tipo="erro">{r.interrompidaMotivo}</Aviso>}

              {r.execucao !== undefined && !r.execucao.liberada && (
                <Aviso tipo="alerta">
                  <strong>Execução bloqueada:</strong> {r.execucao.motivo}
                </Aviso>
              )}

              {podeAprovar && r.estado === 'aguardando_aprovacao' && (
                <Painel titulo="Autorizar">
                  <input
                    placeholder="Motivo (obrigatório para rejeitar)"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                  <div className="linha" style={{ marginTop: 12 }}>
                    <button
                      className="primario encolhe"
                      disabled={acao.executando}
                      onClick={() => chamar('aprovar', {}, 'Remediação aprovada.')}
                    >
                      Aprovar
                    </button>
                    <button
                      className="perigo encolhe"
                      disabled={acao.executando}
                      onClick={() => chamar('rejeitar', { motivo }, 'Remediação rejeitada.')}
                    >
                      Rejeitar
                    </button>
                    <span className="cresce" />
                  </div>
                </Painel>
              )}

              {podeAprovar && r.estado === 'aprovada' && (
                <div className="linha">
                  <button
                    className="primario encolhe"
                    disabled={acao.executando || r.execucao?.liberada === false}
                    onClick={executar}
                  >
                    {acao.executando ? 'Executando…' : 'Executar agora'}
                  </button>
                  <span className="cresce suave">
                    Se não executar agora, o produto executa sozinho assim que as travas permitirem.
                  </span>
                </div>
              )}

              <h3>Ondas</h3>
              {r.ondas === undefined || r.ondas.length === 0 ? (
                <p className="suave">Nenhuma onda executada ainda.</p>
              ) : (
                <div className="rolagem">
                  <table className="densa">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Alvos</th>
                        <th>Sucesso</th>
                        <th>Falha</th>
                        <th>Não entregue</th>
                        <th>runId</th>
                        <th>Início</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.ondas.map((o) => (
                        <tr key={o.id}>
                          <td>{o.onda}</td>
                          <td>{o.alvosTotal}</td>
                          <td>{o.sucessos}</td>
                          <td>{o.falhas}</td>
                          <td>{o.falhasDeEntrega}</td>
                          {/* O runId é a ponte para o log do script no console do KACE. */}
                          <td className="mono">{o.runId ?? '—'}</td>
                          <td className="suave">{data(o.iniciadaEm)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3>Alvos</h3>
              <p className="suave mono" style={{ fontSize: 12.5 }}>
                {r.alvos.map((a) => a.nome).join(', ')}
              </p>
            </>
          )}
        </Estado>
      </div>
    </div>
  );
}
